import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, UserIcon } from 'lucide-react';
export function CustomerForm() {
  const {
    t
  } = useTranslation('customers');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('id');
  const isEditMode = !!customerId;
  const [customerType, setCustomerType] = useState<'company' | 'individual'>('company');
  const [formData, setFormData] = useState({
    // Company
    companyName: isEditMode ? 'Acme Corporation' : '',
    contactPerson: '',
    taxId: '',
    // Individual
    fullName: '',
    // Shared
    email: isEditMode ? 'billing@acme.com' : '',
    phone: isEditMode ? '+66 12 345 6789' : '',
    addressLine1: isEditMode ? '123 Business Street' : '',
    addressLine2: '',
    city: isEditMode ? 'Malé' : '',
    province: '',
    country: 'Maldives',
    postcode: '',
    internalNotes: '',
    status: 'active' as 'active' | 'inactive'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Saving customer:', formData);
    navigate('/customers');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  return <div className="max-w-3xl mx-auto space-y-6 pb-12" data-id="element-4468">
      {/* Header */}
      <div className="flex items-center space-x-4" data-id="element-4469">
        <button onClick={() => navigate('/customers')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-4470">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-4471" />
          {t('pages.CustomerForm.back_to_customers')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4472">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-4473">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-4474">
            {isEditMode ? 'Edit Customer' : 'Add New Customer'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-10" data-id="element-4475">
          {/* SECTION 1: Customer Information */}
          <div className="space-y-6" data-id="element-4476">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-4477">
              {t('pages.CustomerForm.customer_information')}
            </h2>

            {/* Customer Type Toggle */}
            <div className="flex p-1 bg-gray-100 rounded-lg w-fit" data-id="element-4478">
              <button type="button" onClick={() => setCustomerType('company')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${customerType === 'company' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4479">
                <BuildingIcon size={16} className="mr-2" data-id="element-4480" />
                {t('pages.CustomerForm.company')}
              </button>
              <button type="button" onClick={() => setCustomerType('individual')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${customerType === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4481">
                <UserIcon size={16} className="mr-2" data-id="element-4482" />
                {t('pages.CustomerForm.individual')}
              </button>
            </div>

            {customerType === 'company' ? <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-4483">
                <div data-id="element-4484">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4485">
                    {t('pages.CustomerForm.company_name')}
                  </label>
                  <input type="text" name="companyName" required value={formData.companyName} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.enter_company_name')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4486" />
                </div>
                <div data-id="element-4487">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4488">
                    {t('pages.CustomerForm.contact_person')}
                  </label>
                  <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.enter_contact_person')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4489" />
                </div>
                <div className="md:col-span-2" data-id="element-4490">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4491">
                    {t('pages.CustomerForm.tax_id_registration_number')}
                  </label>
                  <input type="text" name="taxId" value={formData.taxId} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.13digit_registration_number')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4492" />
                </div>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-4493">
                <div className="md:col-span-2" data-id="element-4494">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4495">
                    {t('pages.CustomerForm.full_name')}
                  </label>
                  <input type="text" name="fullName" required value={formData.fullName} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.enter_full_name')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4496" />
                </div>
              </div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-4497">
              <div data-id="element-4498">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4499">
                  {t('pages.CustomerForm.email_address')}
                </label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.billingcompanycom')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4500" />
              </div>
              <div data-id="element-4501">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4502">
                  {t('pages.CustomerForm.phone_number')}
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+66 12 345 6789" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4503" />
              </div>
            </div>
          </div>

          {/* Divider: Billing information */}
          <div className="relative py-2" data-id="element-4504">
            <div className="absolute inset-0 flex items-center" data-id="element-4505">
              <div className="w-full border-t border-gray-200" data-id="element-4506"></div>
            </div>
            <div className="relative flex justify-center" data-id="element-4507">
              <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-4508">
                {t('pages.CustomerForm.billing_information')}
              </span>
            </div>
          </div>

          {/* SECTION 2: Billing Information */}
          <div className="space-y-5" data-id="element-4509">
            <div data-id="element-4510">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4511">
                {t('pages.CustomerForm.address_line_1')}
              </label>
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.123_business_street')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4512" />
            </div>
            <div data-id="element-4513">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4514">
                {t('pages.CustomerForm.address_line_2')}
              </label>
              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.suite_building_floor_etc')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4515" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-4516">
              <div data-id="element-4517">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4518">
                  {t('pages.CustomerForm.city')}
                </label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.bangkok')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4519" />
              </div>
              <div data-id="element-4520">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4521">
                  {t('pages.CustomerForm.province')}
                </label>
                <input type="text" name="province" value={formData.province} onChange={handleChange} placeholder={t('pages.CustomerForm.province')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4522" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-4523">
              <div data-id="element-4524">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4525">
                  {t('pages.CustomerForm.country')}
                </label>
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 flex items-center" data-id="element-4526">
                  {t('pages.CustomerForm.thailand')}
                </div>
              </div>
              <div data-id="element-4527">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4528">
                  {t('pages.CustomerForm.postcode')}
                </label>
                <input type="text" name="postcode" value={formData.postcode} onChange={handleChange} placeholder={t('pages.CustomerForm.postcode')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-4529" />
              </div>
            </div>
          </div>

          {/* Divider: Additional information */}
          <div className="relative py-2" data-id="element-4530">
            <div className="absolute inset-0 flex items-center" data-id="element-4531">
              <div className="w-full border-t border-gray-200" data-id="element-4532"></div>
            </div>
            <div className="relative flex justify-center" data-id="element-4533">
              <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-4534">
                {t('pages.CustomerForm.additional_information')}
              </span>
            </div>
          </div>

          {/* SECTION 3: Additional Information */}
          <div className="space-y-5" data-id="element-4535">
            <div data-id="element-4536">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4537">
                {t('pages.CustomerForm.internal_notes')}
              </label>
              <textarea name="internalNotes" value={formData.internalNotes} onChange={handleChange} placeholder={t('pages.CustomerForm.attrs.add_any_internal_notes_about_this')} rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors resize-none" data-id="element-4538" />
            </div>
            <div data-id="element-4539">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-4540">
                {t('pages.CustomerDetail.status_2')}
              </label>
              <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-4541">
                <option value="active" data-id="element-4542">{t('pages.CustomerForm.active')}</option>
                <option value="inactive" data-id="element-4543">
                  {t('pages.CustomerForm.inactive')}
                </option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-4544">
            <button type="button" onClick={() => navigate('/customers')} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-4545">
              {t('pages.CustomerForm.cancel')}
            </button>
            <button type="submit" className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-4546">
              {isEditMode ? 'Update customer' : 'Create customer'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}